"use client";

import {
  Fragment,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Link, useLocation, useNavigate, useParams, Outlet } from "react-router-dom";
import { fetchWrapper } from "services/api-client/fetchWrapper";
import { apiBaseUrl, eApplicationSource, GUEST_USER_ALLOWED } from "utils/constants";
import { isEmpty, joinWithSpace, shareConversation, stripMarkdown } from "utils/helpers";
import "react-confirm-alert/src/react-confirm-alert.css";
import { captureException } from "scripts/logging/error-report";
import { BookmarkContext, BookmarkType } from "services/BookmarkContext";
import { UserContext, IUser } from "services/UserContext";
import bootbox from "bootbox";
import {
  ReasoningEffort,
  defaultModel,
  getStoredModel,
  getStoredReasoningEffort,
  isReasoningEffort,
  models,
  reasoningEfforts,
  storeModel,
  storeReasoningEffort,
  supportsReasoningEffort,
} from "utils/models";
import { ModelPriceDropdown } from "components/ModelPriceDropdown";
import { useQueryFlags } from "scripts/hooks/useQueryFlags";
import { useMatomoPageView } from "scripts/hooks/useMatomoPageView";
import { setMatomoUserId, matomoUserId } from "services/api-client/matomoTracker";
import { isListingPath } from "utils/routes";
import { useIsStandardUUID } from 'scripts/hooks/useIsStandardUUID';
import { ShareContext } from "services/ShareContext";
import { MainHeader } from "components/MainHeader";
import { useWindowResize } from "scripts/hooks/useWindowResize";
import { useBodyClass } from "scripts/hooks/useElementClass";
import ErrorBoundary from "components/ErrorBoundary";
import { SignInModal } from "components/SignInModal";
import { SidebarSignUp } from "components/SidebarSignUp";
import { SupportChat } from "components/SupportChat";
import { buildUserMenuItems } from "utils/userMenu";
import { websiteProductionHost } from "scripts/application/setting";

export type IAssistant = IAssistant.Input &
  Required<Pick<IAssistant.Input, "id">>;

export namespace IAssistant {
  export interface Input {
    id?: string;
    type: string;
    name: string;
    sessionId?: string;
  }
}

export interface IChatSessionGroup {
  date: string;
  items: IChatSession[];
}

export interface IChatSession {
  session_id: string;
  title: string;
  harid?: string;
}

export interface IDefaultLayoutProps {
  assistant?: IAssistant;
  onUserChange?(user: IUser): void;
  statusCode?: number;
}

interface IServiceData<T> {
  loading: boolean;
  data: T | undefined;
}

// Read the persisted sidebar open/closed state, defaulting to open when it is
// unset or unreadable.
function getSavedSidebarState(): boolean {
  try {
    const savedState = localStorage.getItem('har_ai_sidebar_opened');
    return savedState !== null ? JSON.parse(savedState) : true;
  } catch {
    return true;
  }
}

// Session management hook to prevent unnecessary re-renders
function useSessionManager() {
  const [sessions, setSessions] = useState<IServiceData<IChatSessionGroup[]>>({
    loading: false,
    data: [{ date: "", items: [] }],
  });
  const [sessionsLoaded, setSessionsLoaded] = useState(false);

  const { embed } = useQueryFlags();

  const getSessions = useCallback(async (signal?: AbortSignal) => {
    const response = await fetch(apiBaseUrl + `/api/v1/chatbot/sessions`, {
      signal,
      credentials: "include",
    });

    if (response.status !== 200) {
      return undefined;
    }
    return response.json();
  }, []);

  const loadSessions = useCallback(async (signal?: AbortSignal, assistantId?: string, force?: boolean) => {
    if (!assistantId || embed) return;
    // Skip loading if sessions are already loaded unless forced
    if (sessionsLoaded && !force) return;

    try {
      setSessions({ loading: true, data: undefined });
      const sessionsData = await getSessions(signal);
      if (sessionsData === undefined || sessionsData?.items?.length === 0) {
        setSessions({ loading: false, data: undefined });
        setSessionsLoaded(true);
        return;
      }
      setSessions((prev) => ({
        loading: false,
        data: prev.data ? [...prev.data, sessionsData] : sessionsData,
      }));
      setSessionsLoaded(true);
    } catch (e: unknown) {
      captureException(e);
      setSessions({ loading: false, data: undefined });
      if (e !== "cleanup") {
        throw e;
      }
    }
  }, [embed, getSessions, sessionsLoaded]);

  const createAssistantSession = useCallback((date: string, session: IChatSession) => {
    setSessions(prevSessions => {
      const currentSessions = prevSessions.data ?? [];
      // Find existing session group with the same date
      const existingSessionGroup = currentSessions.find(group => group.date === date);

      if (existingSessionGroup) {
        // Add to existing group (most recent sessions go first)
        existingSessionGroup.items.unshift(session);
      } else {
        // Create new group and add at the beginning (most recent groups go first)
        currentSessions.unshift({ date, items: [session] });
      }
      return { ...prevSessions, data: currentSessions };
    });
  }, []);

  const deleteSession = useCallback((sessionId: string) => {
    setSessions(prevSessions => {
      const currentSessions = prevSessions.data ?? [];
      const updatedSessions = currentSessions.map(group => ({
        ...group,
        items: group.items.filter(item => item.session_id !== sessionId)
      })).filter(group => group.items.length > 0); // Remove empty groups

      return { ...prevSessions, data: updatedSessions };
    });
  }, []);

  return {
    sessions,
    loadSessions,
    createAssistantSession,
    deleteSession,
  };
}

// User management hook to prevent unnecessary re-renders
function useUserManager() {
  const [user, setUser] = useState<IUser>({});
  const { cId } = useQueryFlags();

  const getUser = useCallback(async (signal: AbortSignal) => {
    setUser((prev) => ({ ...prev, loading: true }));
    try {
      const url = apiBaseUrl + "/api/v1/chatbot/user" + (cId ? `?cid=${encodeURIComponent(cId)}` : "");
      const response = await fetch(url, {
        credentials: "include",
        signal,
      });
      const data = await response.json();
      setUser({ ...data, loading: false });
    } catch (e) {
      if (e === "cleanup") {
        return;
      }
      console.error(e);
      captureException(e);
      // Clear the loading flag so guards keyed on it don't hide UI forever.
      setUser((prev) => ({ ...prev, loading: false }));
    }
  }, [cId]);

  useEffect(() => {
    const aborter = new AbortController();
    getUser(aborter.signal);
    return () => aborter.abort("cleanup");
  }, [getUser]);

  return { user, setUser };
}

// Assistant state management
function useAssistantManager() {
  const [assistant, setAssistantState] = useState<IAssistant | undefined>();
  const { session } = useParams();

  // Pages register their assistant from a layout effect, so this callback must
  // keep a stable identity across assistant changes (otherwise it would retrigger
  // those effects) and must not read `assistant` from the closure — hence the
  // functional update, which also keeps the previous object when nothing changed.
  const setAssistant = useCallback((value: IAssistant.Input): void => {
    const newAssistant: IAssistant = {
      ...value,
      id: value.id ?? value.type,
      sessionId: session
    };

    setAssistantState((current) =>
      current?.id === newAssistant.id &&
        current?.sessionId === newAssistant.sessionId
        ? current
        : newAssistant
    );
  }, [session]);

  return { assistant, setAssistant };
}

const AISearchLogo = (
  <Link to={'/'} className="text_logo text-decoration-none d-none">
    <img
      className="text_logo_img"
      src="https://content.harstatic.com/media/icons/common/HAR_AI_Logo.svg"
    />
    <span className="text_logo_text">Search</span>
  </Link>
);

// Default Layout Component with optimized rendering
const DefaultLayoutComponent = () => {
  const {
    embed,
    hideHeader,
    hideFooter,
    search: locationSearch,
    hideShare,
    isContentNotVisible
  } = useQueryFlags();

  // Use optimized hooks
  const { user, setUser } = useUserManager();
  const { assistant, setAssistant } = useAssistantManager();
  const { sessions, loadSessions, createAssistantSession, deleteSession } = useSessionManager();

  const [statusCode, setStatusCode] = useState(200);
  const [bookmark, setBookmark] = useState<BookmarkType | null>(null);
  const listingDetailShareUrlRef = useRef<string | null>(null);

  const { harid, session, token } = useParams();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const isLikelyChatSession = useIsStandardUUID();
  const isHeaderNotVisible = embed || hideHeader;
  // Both pickers are staff-only and persist to localStorage; ChatWindow reads
  // the stored values when it builds the request. The model is state rather
  // than a bare read because the reasoning picker below is only rendered for
  // the models that read it, so switching model has to re-render the header.
  const [selectedModel, setSelectedModel] = useState<string>(
    () => getStoredModel() || defaultModel,
  );
  const [reasoningEffort, setReasoningEffort] = useState<ReasoningEffort>(
    getStoredReasoningEffort,
  );
  const isShareButtonDisabled = isListingPath(pathname) && !session;
  const [isLeftPaneVisible, setIsLeftPaneVisible] = useState(false);
  const { width } = useWindowResize();
  // Initialize from the persisted value so the first paint already matches the
  // user's last choice. Starting with a hard-coded `true` caused a closed
  // sidebar to flash open and then snap shut once the effect below ran.

  const [isLeftMenuOpen, setIsLeftMenuOpen] = useState<boolean>(getSavedSidebarState);
  // Controls the mobile HAR navigation drawer (the MainHeader `#MainNavBar`
  // collapse). Opening it adds `menu_opened` to <body>, which the existing
  // layout CSS turns into a right-side slide-in drawer.
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false);

  // Load sessions once when user is authenticated (not on every assistant change)
  useEffect(() => {
    if (user.user_id && !embed) {
      const aborter = new AbortController();
      loadSessions(aborter.signal, 'search'); // Use 'search' as default assistant type for loading sessions
      return () => aborter.abort("cleanup");
    }
  }, [user.user_id, loadSessions, embed]);

  // The first page view fires before the user context resolves, so hand Matomo
  // the username as soon as it is known: it applies to the rest of the visit,
  // including the session recording already in progress.
  //
  // Dropped again while a reload is in flight — see `matomoUserId`, which the
  // event call sites ask the same question of, so the layout and an event
  // fired in that window cannot disagree about who the visitor is.
  useEffect(() => {
    setMatomoUserId(matomoUserId(user));
  }, [user.username, user.loading]);

  // A logged-out visitor is represented as a "guest" user that still has a
  // user_id, so checking user_id alone is not enough to detect authentication.
  const isLoggedIn = !!user.user_id && user.role !== "guest";
  const isGuestOrUnknown = !user || !user.role || user.role === 'guest';
  const blockedByGuestAccess = !isContentNotVisible && !GUEST_USER_ALLOWED && isGuestOrUnknown;

  // Menu state management
  useEffect(() => {
    // Small screens always show the expanded sidebar.
    if (width < 768) {
      setIsLeftMenuOpen(true);
      return;
    }
    // Wait until the user fetch resolves before deciding. While it is still
    // pending the user looks like a guest, and forcing the sidebar open here
    // caused a closed sidebar to flash open and then snap shut once auth
    // resolved.
    if (user.loading !== false) return;
    // Guests always get the expanded sidebar; authenticated users keep their
    // persisted preference.
    setIsLeftMenuOpen(isLoggedIn ? getSavedSidebarState() : true);
  }, [width, isLoggedIn, user.loading]);

  // Reveal/hide the mobile navigation drawer by toggling the `menu_opened`
  // body class the layout CSS keys off of.
  useBodyClass(isMainMenuOpen && "menu_opened");

  // The drawer is mobile-only; if the viewport grows to desktop while it is
  // open, close it so `menu_opened` (which locks body scroll) is not left on.
  useEffect(() => {
    if (width >= 768 && isMainMenuOpen) {
      setIsMainMenuOpen(false);
    }
  }, [width, isMainMenuOpen]);

  // Body class management
  useBodyClass(isHeaderNotVisible && "hide-header");
  useBodyClass(hideFooter && "hide-footer");

  // Let the CSS adjust the layout for logged-out visitors. Wait until the user
  // fetch resolves, otherwise the pending state looks like a guest and the
  // class flashes on for authenticated users.
  useBodyClass(user.loading === false && !isLoggedIn && "not_loggedin");

  useEffect(() => {
    const query = new URLSearchParams(search);
    const source = query.get('source')?.trim()?.toLowerCase() as eApplicationSource | null;
    const allSources = Object.values(eApplicationSource);
    if (!source || !allSources.includes(source)) return;
    const embedClass = `${source}-embed`;
    document.body.classList.add(embedClass);
    // Taken off again when the query stops naming this source. The New-chat
    // button navigates to a bare "/", so an embedded visitor really does leave
    // the source behind mid-session — and without this the class stayed on,
    // rendering a page whose URL no longer claims that layout.
    return () => document.body.classList.remove(embedClass);
  }, [search]);

  // Matomo page views for every route: they drive the Pages report and, more
  // importantly, tell Heatmap & Session Recording that a navigation happened.
  //
  // Declared after the body-class effects above, because effects run in the
  // order they were declared and a page view starts a recording: reporting it
  // first would open the recording on a DOM that has not had the layout class
  // applied yet, so the replay's opening frames show a layout the visitor
  // never saw.
  useMatomoPageView();

  // Get bookmark data
  useEffect(() => {
    const aborter = new AbortController();
    getBookmark(aborter.signal);
    return () => aborter.abort("cleanup");
  }, []);

  const getBookmark = async (signal?: AbortSignal) => {
    try {
      const result = await fetchWrapper.get("/api/v1/chatbot/bookmark", signal);
      setBookmark({ mlsnums: result?.mlsnums });
    } catch (e) {
      captureException(e);
    }
  };

  const handleDeleteSession = useCallback((
    sessionid: string,
    title: string,
    harid?: string
  ) => {
    bootbox.confirm({
      message: `This will delete chat "${title}"`,
      className: "bootbox--custom p-4",
      closeButton: true,
      buttons: {
        confirm: {
          label: "YES",
          className:
            "btn btn--primary btn--medium order-0 confirm-button-custom-bootbox",
        },
        cancel: {
          label: "Cancel",
          className:
            "btn btn--shapeless btn--medium order-1 close-button-custom-bootbox",
        },
      },
      callback: async (result) => {
        if (result) {
          try {
            const response = await fetchWrapper._delete(
              `/api/v1/chatbot/threads/${sessionid}`
            );
            if (response?.status) {
              if (assistant?.sessionId === sessionid) {
                navigate("/");
              } else {
                deleteSession(sessionid);
              }
            }
          } catch (e) {
            if (e === "cleanup") {
              return;
            }
            console.error(e);
            captureException(e);
          }
        }
      },
    });
  }, [assistant?.sessionId, navigate, deleteSession]);

  const handleNewChatSession = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/");
    setStatusCode(200);
    setIsLeftPaneVisible(false);
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    try {
      const result = await fetch(apiBaseUrl + "/api/v1/chatbot/logout", {
        credentials: "include",
      });
      // fetch resolves even on non-2xx statuses, so guard on result.ok to
      // avoid clearing the user / redirecting when logout actually failed.
      if (!result.ok) {
        captureException(new Error(`Logout failed with status ${result.status}`));
        return;
      }
      setUser({});
      // Guests are always shown the expanded sidebar (handled by the menu
      // state effect), so no localStorage reset is needed here.
      setIsLeftMenuOpen(true);
      window.location.href = "/";
    } catch (e) {
      captureException(e);
    }
  }, [setUser]);

  // Both pickers only move once the choice is persisted. The chat request reads
  // these back from storage at send time, so on a blocked write (private-mode
  // Safari, site data off) keeping the old state snaps the select back to the
  // override that is actually in effect rather than advertising one that is not.
  const onModelChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const model = e.target.value;
    if (!storeModel(model)) return;
    setSelectedModel(model);
  }, []);

  const onReasoningEffortChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const effort = e.target.value;
      // The <select> only ever offers valid values; the guard is here so the
      // narrowing is real rather than a cast, and a stale option can never be
      // persisted for the chat payload to send.
      if (!isReasoningEffort(effort)) return;
      if (!storeReasoningEffort(effort)) return;
      setReasoningEffort(effort);
    },
    [],
  );

  const openSidebar = useCallback(() => {
    setIsLeftPaneVisible(true);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsLeftPaneVisible(false);
  }, []);

  const handleLeftMenuToggle = useCallback(() => {
    // On small screens the sidebar is forced expanded (see the menu-state
    // effect), so ignore toggle attempts to keep the behavior consistent.
    if (width < 768) return;
    setIsLeftMenuOpen((prev) => {
      const newState = !prev;
      try {
        localStorage.setItem('har_ai_sidebar_opened', JSON.stringify(newState));
      } catch {
        console.warn('Could not save sidebar state to localStorage');
      }
      return newState;
    });
  }, [width]);

  const onNewAssistantSession = useCallback((date: string, session: IChatSession) => {
    if (assistant) {
      setAssistant({ ...assistant, sessionId: session.session_id });
    }
    createAssistantSession(date, session);
  }, [assistant, setAssistant, createAssistantSession]);

  // Memoized Header component
  const Header = useMemo(() => {
    const isGuest = (user?.role_id ?? 0) === 0;
    return (
      <div
        className={!user?.is_staff ? "ai_user" : "ai_user pt-2"}
      >
        <div className="d-flex align-items-center ai_user_container">
          {user.user_id && !isGuest && (
            <>
              {!!user.is_staff && (
                <>
                  <ModelPriceDropdown />
                  <select
                    className="custom-select nfilter custom-select_medium"
                    value={selectedModel}
                    onChange={onModelChange}
                  >
                    {Object.entries(models).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {/* Only the models that read the setting get the picker.
                      Reasoning is not the test: gpt-5-mini reasons too, but on
                      chat completions, where reasoning_effort is ignored -- so
                      the control would be inert there. */}
                  {supportsReasoningEffort(selectedModel) && (
                    <select
                      className="custom-select nfilter custom-select_medium ml-2"
                      title="Reasoning effort"
                      aria-label="Reasoning effort"
                      value={reasoningEffort}
                      onChange={onReasoningEffortChange}
                    >
                      {Object.entries(reasoningEfforts).map(([value, label]) => (
                        <option key={value} value={value}>
                          {`Reasoning: ${label}`}
                        </option>
                      ))}
                    </select>
                  )}
                </>
              )}

              {/* Session recordings replay the DOM, so keep the account menu
                  (name, photo, profile links) out of the recording. */}
              <div
                className="dropdown dropdown dropdown--custom dropdown--custom_medium ai_user_menu mb-0"
                data-matomo-mask
              >
                <a
                  className="cpf_user_menu"
                  href="#"
                  id="dropdownMenuButton"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  <div className="d-flex align-items-center">
                    <span
                      className="border_radius_8 bg--image"
                      style={{
                        width: 40,
                        height: 40,
                        backgroundImage: `url(${isEmpty(user.photo)
                          ? "https://content.harstatic.com/media/icons/a_all/agent_placeholder_square.svg"
                          : user.photo
                          })`,
                      }}
                    />
                  </div>
                </a>
                <div
                  className="dropdown-menu user-dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  {buildUserMenuItems(user).map((item) =>
                    item.isStatic ? (
                      <a
                        key={item.label}
                        className={joinWithSpace("dropdown-item", item.className)}
                      >
                        {item.label}
                      </a>
                    ) : (
                      <a
                        key={item.label}
                        className={joinWithSpace("dropdown-item", item.className)}
                        href={`${websiteProductionHost}${item.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.label}
                        {item.isNew && (
                          <>
                            &nbsp;
                            <span className="border_radius--default bg_color_pending pl-2 pr-2 ps-2 pe-2 pt-1 pb-1 color_snow_white font_weight--semi_bold text-center justify-content-center">
                              NEW
                            </span>
                          </>
                        )}
                      </a>
                    )
                  )}

                  <a
                    className="dropdown-item border-top mt-2 pt-3"
                    onClick={handleLogout}
                    style={{ cursor: "pointer" }}
                  >
                    Sign out
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }, [
    user,
    selectedModel,
    onModelChange,
    reasoningEffort,
    onReasoningEffortChange,
    handleLogout,
  ]);


  return (
    <UserContext.Provider value={{ user, setUser }}>
      <ShareContext.Provider value={{ statusCode, setStatusCode, listingDetailShareUrlRef }}>
        <BookmarkContext.Provider value={{ bookmark, setBookmark }}>
          <div className="ai_mode">
            {isHeaderNotVisible ? null : (
              <MainHeader
                onCloseMenu={() => setIsMainMenuOpen(false)}
                onSignOut={handleLogout}
              >
                {Header}
              </MainHeader>
            )}
            <div className={`ai_layout har_ai_search${isLeftMenuOpen ? "" : " leftmenu_closed"}`}>
              <div className="ai_container">
                {(statusCode === 404 && !isLikelyChatSession && assistant) || !assistant ? null :
                  !embed && (<>
                    <SignInModal />
                    {!blockedByGuestAccess && (<>
                      <div className={`ai_left_pane${isLeftPaneVisible ? " visible" : ""}`}>
                        <div className="d-flex flex-column h-100">
                          <div className="py-4 px-3">
                            <div className="row no-gutters">
                              <div className="col-6">
                                {!!user.user_id && user?.role !== "guest" && (
                                  <div>
                                    <img
                                      className="left_menu_toggle"
                                      src={
                                        isLeftMenuOpen
                                          ? "https://content.harstatic.com/media/icons/a_all/left_menu_close.svg"
                                          : "https://content.harstatic.com/media/icons/a_all/left_menu_open.svg"
                                      }
                                      alt="Toggle Icon"
                                      onClick={handleLeftMenuToggle}
                                    />
                                  </div>
                                )}
                                {isLeftPaneVisible ? (
                                  <div onClick={closeSidebar} className="mt-2">
                                    <img
                                      className="w-22px"
                                      src={"https://content.harstatic.com/media/icons/close_black.svg"}
                                      alt="Close Icon"
                                    />
                                  </div>
                                ) : AISearchLogo}
                              </div>
                              {assistant && (
                                <div className="col-6 text-right">
                                  <a
                                    style={{ opacity: "0.6" }}
                                    className="d-inline-flex text-decoration-none font_size--small color_black font_weight--semi_bold align-items-center btn__bordered"
                                    href="#"
                                    onClick={handleNewChatSession}
                                  >
                                    <img
                                      className="mr-2"
                                      style={{ width: 18 }}
                                      src="https://content.harstatic.com/media/icons/a_all/add_block_3.svg"
                                    />{" "}
                                    <span className="pt-1" style={{ color: "#000" }}>New</span>
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                          {assistant && (
                            <div className="d-flex flex-column flex-grow-1 overflow-auto left_pane_body">
                              {sessions.loading && (
                                <div className="ml-3">
                                  <div
                                    className="spinner-border"
                                    style={{ width: "16px", height: "16px" }}
                                    role="status"
                                  ></div>
                                  <span
                                    className="ml-2"
                                    style={{ verticalAlign: "top" }}
                                  >
                                    Loading chat history...
                                  </span>
                                </div>
                              )}

                              {!sessions.loading && sessions.data && (
                                <div className="cphl_container px-3">
                                  {sessions.data?.map(({ date, items }) => {
                                    const search = locationSearch;
                                    return (
                                      <Fragment key={date}>
                                        <div className="font_size--medium font_weight--semi_bold color_lighter">
                                          {date}
                                        </div>

                                        <div className="mb-4">
                                          {items?.map(({ session_id, title, harid }) => {
                                            const rawTitle = title ? stripMarkdown(title) : "Property Search Results";
                                            return (
                                              <div
                                                key={session_id}
                                                className={joinWithSpace(
                                                  "cphl_item",
                                                  session_id === assistant.sessionId &&
                                                  "active"
                                                )}
                                              >
                                                <div
                                                  onClick={() => {
                                                    navigate(
                                                      harid
                                                        ? `/listing/${harid}/${session_id}${search}`
                                                        : `/${session_id}${search}`,
                                                      { replace: true }
                                                    );
                                                    setIsLeftPaneVisible(false);
                                                  }}
                                                  className="cphl_item_text overflow-ellipsis"
                                                  title={rawTitle}
                                                >
                                                  {isEmpty(title)
                                                    ? "Property Search Results"
                                                    : rawTitle}
                                                </div>

                                                <div
                                                  style={{
                                                    backgroundColor:
                                                      session_id === assistant.sessionId
                                                        ? "#e3e5ed"
                                                        : "rgba(218, 225, 248, 0.4)",
                                                  }}
                                                  className=" cphl_item_dropdown dropdown dropdown--custom dropdown--custom_medium"
                                                >
                                                  <a
                                                    className="text-decoration-none font_size--medium hover-opacity hover--color-auxiliary color_auxiliary pl-2 pr-2"
                                                    href="#"
                                                    role="link"
                                                    id="dropdownMenuLink"
                                                    data-toggle="dropdown"
                                                    aria-haspopup="true"
                                                    aria-expanded="true"
                                                  >
                                                    <img
                                                      alt="more"
                                                      src="https://content.harstatic.com/media/icons/copilot/dote_vertical_dark.svg"
                                                    />
                                                  </a>

                                                  <div
                                                    x-placement="bottom-start"
                                                    aria-labelledby="dropdownMenuLink"
                                                    className="dropdown-menu dropdown-menu__icon animate slideIn dropdown-menu"
                                                    data-popper-reference-hidden="true"
                                                    data-popper-escaped="true"
                                                    data-popper-placement="bottom-start"
                                                  >
                                                    <a
                                                      className="dropdown-item pt-1 pb-1 hover--color-sold color-sold"
                                                      onClick={() =>
                                                        handleDeleteSession(
                                                          session_id,
                                                          title,
                                                          harid
                                                        )
                                                      }
                                                    >
                                                      Delete
                                                    </a>
                                                  </div>
                                                </div>
                                              </div>
                                            )
                                          })}
                                        </div>
                                      </Fragment>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                          {!blockedByGuestAccess && <SidebarSignUp user={user} assistant={assistant} />}
                        </div>
                      </div>
                      {/* hamburger menu for mobile view */}
                      <div className="top_contols menu_btns">
                        <div
                          className="ai_har_menu"
                          onClick={() => setIsMainMenuOpen(true)}
                          role="button"
                          aria-label="Open navigation menu"
                          style={{ cursor: "pointer" }}
                        >
                          <img
                            className="w-22px"
                            src={"https://content.harstatic.com/media/icons/menu_icon_humberger2.svg"}
                            alt="Hamburger Toggle Icon"
                          />
                        </div>
                        {!isLeftPaneVisible && (
                          <div className="ai_humberger" onClick={openSidebar}>
                            <img
                              className="w-22px"
                              src={"https://content.harstatic.com/media/icons/copilot/history-icon.svg"}
                              alt="Hamburger Toggle Icon"
                            />
                          </div>
                        )}
                      </div>
                      {/* ai search logo for mobile view  */}
                      <div className="resp_logo">
                        {AISearchLogo}
                      </div>
                    </>)}
                  </>)}

                <div className="ai_right position-relative">
                  <div className="cb_container container">
                    <div className="cb_content">
                      <div className="chat_floating_actions">
                        {/* Agent-only support chat, mounted on every route; the
                            component gates itself on role, embed mode and whether
                            this host is allowed to frame the Salesforce chat. */}
                        <SupportChat user={user} className="m-0" />
                        {(session || harid || token) && !hideShare && [200, 201].includes(statusCode) && (
                          <span title={isShareButtonDisabled ? "Please start the conversation first to share" : ""} className={`btn_icon btn_header m-0 d-block`} style={{ cursor: isShareButtonDisabled ? 'not-allowed' : 'pointer' }} onClick={() => shareConversation(session || '', harid)}>
                            <img
                              style={{
                                width: 30,
                                height: 30,
                              }}
                              src="https://content.harstatic.com/media/icons/copilot/har-ai-icon-share.svg"
                              alt="share-icon"
                            />
                          </span>
                        )}
                      </div>
                      <div
                        className="cpr_container"
                        style={{
                          //paddingBottom: hideFooter ? "1rem" : "", it effecting in many places we need to hide this change
                        }}
                      >
                        {/* This is where the page content will be rendered */}
                        <ErrorBoundary>
                          <Outlet context={{
                            user,
                            setAssistant,
                            onNewAssistantSession,
                            statusCode,
                            blockedByGuestAccess
                          }} />
                        </ErrorBoundary>
                      </div>
                      {!hideFooter && (
                        <div>
                          {/* for web */}
                          <div className="footer_disclaimer font_size--small_extra color_auxiliary text-center pt-2 d-md-block d-none">
                            AI Mode search results can be wrong. Confirm key data.{" "}
                            <a
                              className="color_auxiliary"
                              href="#"
                              onClick={showPrivacyModal}
                            >
                              &nbsp;HAR.AI Privacy Notice
                            </a>
                          </div>

                          {/* for mobile  */}
                          <div className="footer_disclaimer font_size--small_extra_extra color_auxiliary text-center pt-2 d-md-none d-block">
                            AI Mode results can be wrong. Confirm key data.{" "}
                            <a
                              className="color_auxiliary"
                              href="#"
                              onClick={showPrivacyModal}
                            >
                              &nbsp;HAR.AI Privacy Notice
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {!embed && isLeftPaneVisible && (
                <div className="menu_overlay_history"></div>
              )}
            </div>

            {!embed && (
              <div
                className={`menu_overlay_history ${isLeftPaneVisible ? "fade-in" : ""}`}
              ></div>
            )}
          </div>
        </BookmarkContext.Provider>
      </ShareContext.Provider>
    </UserContext.Provider>
  );
};

const showPrivacyModal = (
  e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
) => {
  e.preventDefault();

  bootbox.alert({
    title: "Disclaimer for Use <br> of AI Generated Content",
    message: `<div class="text-left font_size--medium">
                      HAR.AI was created using Artificial Intelligence technology. While efforts have been made to ensure accuracy, there may be errors. Please carefully review and verify the accuracy and appropriateness of all elements of these outputs. You are ultimately responsible for ensuring that all descriptions associated with your profile accurately reflect your qualifications and comply with all applicable laws and regulations. HAR.com assumes no liability for any errors or omissions and you agree to hold HAR.com harmless for any loss or damage arising from your use of or reliance on the technology. For more information on the use of this technology, please visit our <a class="text-decoration-none" href="https://cms.har.com/privacy-policy/" target="_blank">privacy policy</a> or contact us at <a class="text-decoration-none" href="mailto:privacy@har.com">privacy@har.com</a>.
                    </div>`,
    centerVertical: true,
    closeButton: true,
    size: "large",
    className: "privacy-modal-bootbox",
    backdrop: true,
  });
};

export default DefaultLayoutComponent;
